import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { Router } from '@angular/router';
import {
  DisplayedPaidMealList,
  DisplayedPreOrderMealList,
  DisplayedSpecialMealList,
  InnerSpecialMealList,
  ListSpecialMeal,
  MealCatalogItem,
  PassengerApplicationStatusMap,
  SegmentMap,
} from '@common/interfaces/mybooking/flight-meal.interface';
import { FlightMealService } from '@common/services/my-booking/flight-meal-service/flight-meal-service';
import { GetOrderState } from '@common/store/get-order/get-order.state';
import { SupportModalBlockComponent } from '@lib/components/support-class/support-modal-block-component';
import { CommonLibService } from '@lib/services/common-lib/common-lib.service';
import { GetMealResponseDataDDChargeableMealListInner } from 'src/sdk-servicing/model/getMealResponseDataDDChargeableMealListInner';
import { GetMealResponseDataDDPreorderMealListInner } from 'src/sdk-servicing/model/getMealResponseDataDDPreorderMealListInner';
import { SelectMenuPayload } from './select-menu-payload.state';
import { Subscription } from 'rxjs';
import { parseEmptyArray, isEmptyArray } from '@common/helpers';
import {
  Bound,
  GetMealResponseData,
  GetMealResponseDataD,
  GetMealResponseDataDD,
  GetMealResponseDataDDAppliedMealListInner,
  GetOrderResponseData,
  GetOrderResponseDataAir,
  GetOrderResponseDataOrderType,
  OrderSeatItemSeatSelection,
  Traveler,
  Type1,
} from 'src/sdk-servicing';
import {
  AppliedMealList,
  FlightMealInfo,
  Meal,
  TravelerMealInfo,
} from '@common/interfaces/mybooking/order-services.interface';
import { v4 as uuidV4 } from 'uuid';
import { MyBookingServiceService } from '@common/services/my-booking/my-booking-service/my-booking-service.service';
import { DialogClickType } from '@lib/interfaces';

@Component({
  selector: 'asw-select-menu',
  templateUrl: './select-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectMenuComponent extends SupportModalBlockComponent {
  public override payload: SelectMenuPayload | null = {};
  travelerId: string = '';
  flightId: string = '';
  normalInFlightMealMenuDetailsLabel: string = '';
  getOrderServicingCommon!: GetOrderState;

  appliedMeal!: TravelerMealInfo;
  displayedPaidMealList!: Array<DisplayedPaidMealList>;
  displayedPreOrderMealList!: Array<DisplayedPreOrderMealList>;
  displayedSpecialMealList!: Array<DisplayedSpecialMealList>;
  _subscriptions: Subscription = new Subscription();
  pnr!: GetOrderResponseData;
  meal!: GetMealResponseData;
  flights!: Type1[];

  uuid: string = uuidV4();

  constructor(
    private _common: CommonLibService,
    private _router: Router,
    private _flightMealService: FlightMealService,
    private _change: ChangeDetectorRef,
    private _myBookingServiceService: MyBookingServiceService
  ) {
    super(_common);
    this.closeWithUrlChange(this._router);
  }

  init() {
    this._subscriptions.add(
      this._flightMealService.inFlightMealRequestData$.subscribe((data) => {
        this.pnr = data.pnr ?? ({} as GetOrderResponseData);
        this.meal = data.meal ?? ({} as GetMealResponseData);

        this.flights =
          this.pnr?.air?.bounds?.flatMap((bound: Bound) => bound.flights ?? ([] as Type1[])) ?? ([] as Type1[]);

        this.travelerId = this.payload?.travelerId ?? '';
        this.flightId = this.payload?.flightId ?? '';
        this.normalInFlightMealMenuDetailsLabel = this.getNormalInFlightMealMenuDetailsLabel();
        this.appliedMeal = ((this.pnr?.services?.meal as Meal)?.[this.flightId!] as FlightMealInfo)?.[
          this.travelerId
        ] as TravelerMealInfo;

        this.displayedPaidMealList = this.getDisplayedPaidMealList(this.meal);
        this.displayedPreOrderMealList = this.getDisplayedPreOrderMealList(this.meal);
        this.displayedSpecialMealList = this.getDisplayedSpecialMealList(this.meal);
        this._change.markForCheck();
      })
    );
  }

  get isDisplayPaidMealInformation(): boolean {
    const chargeableMealList =
      (this.meal?.[`${this.travelerId}`]?.[`${this.flightId}`]
        ?.chargeableMealList as Array<GetMealResponseDataDDChargeableMealListInner>) ??
      ([] as Array<GetMealResponseDataDDChargeableMealListInner>);
    return isEmptyArray(chargeableMealList) !== true;
  }

  get isDisplayPreOrderMealInformation(): boolean {
    const preorderMealList =
      (this.meal?.[`${this.travelerId}`]?.[`${this.flightId}`]
        ?.preorderMealList as Array<GetMealResponseDataDDPreorderMealListInner>) ??
      ([] as Array<GetMealResponseDataDDPreorderMealListInner>);
    return isEmptyArray(preorderMealList) !== true;
  }

  getNormalInFlightMealMenuDetailsLabel() {
    const cabin = this.flights.find((flight) => flight.id === this.flightId)?.cabin;
    // label.mealMenuDetail.normal.c	Flight menu.cabin = "business"
    // label.mealMenuDetail.normal.f	Flight menu.cabin = "first"
    // label.mealMenuDetail.normal.py	Flight menu.cabin = "ecoPremium"
    // label.mealMenuDetail.normal.y	Flight menu.cabin = "eco"
    switch (cabin) {
      case GetOrderResponseDataAir.HighestCabinClassEnum.Business:
        return 'label.mealMenuDetail.normal.c';
      case GetOrderResponseDataAir.HighestCabinClassEnum.First:
        return 'label.mealMenuDetail.normal.f';
      case GetOrderResponseDataAir.HighestCabinClassEnum.EcoPremium:
        return 'label.mealMenuDetail.normal.py';
      case GetOrderResponseDataAir.HighestCabinClassEnum.Eco:
        return 'label.mealMenuDetail.normal.y';
      default:
        return '';
    }
  }

  getDisplayedPaidMealList(mealData: GetMealResponseData) {
    const displayedPaidMealList: DisplayedPaidMealList[] = [];
    const isExistAncillary = this.appliedMeal?.appliedMealList.some(
      (item: AppliedMealList) => item.type === TravelerMealInfo.MealType.ANCILLARY
    );
    const chargeableMealList =
      (mealData?.[`${this.travelerId}`]?.[`${this.flightId}`]
        ?.chargeableMealList as Array<GetMealResponseDataDDChargeableMealListInner>) ??
      ([] as Array<GetMealResponseDataDDChargeableMealListInner>);

    if (isExistAncillary) {
      chargeableMealList.forEach((chargeableMeal: GetMealResponseDataDDChargeableMealListInner, index: number) => {
        let isActive = false;
        // 申込済機内食情報が存在するかつ、申込済機内食情報.type=""ancillary"" (有料機内食)の場合、申込済機内食情報.code= 有料機内食情報.codeとなる機内食メニューのみ活性とし、他の有料機内食情報.codeを非活性で表示する。
        for (let appliedMeal of parseEmptyArray<AppliedMealList>(this.appliedMeal?.appliedMealList)) {
          if (chargeableMeal.code === appliedMeal.code) {
            isActive = true;
            break;
          }
        }
        displayedPaidMealList.push({
          isActive,
          ...chargeableMeal,
          type: TravelerMealInfo.MealType.ANCILLARY,
          // 当該有料機内食情報.codeを引数として、「当画面共通処理No3.有料機内食名称取得処理」を行い、返却値の有料機内食名称を表示する。
          mealName: this._flightMealService.onHandleCommonNo3(chargeableMeal?.code ?? ''),
        });
      });
    } else {
      // 上記以外の場合、全て有料機内食情報.codeを活性で表示する。
      chargeableMealList.forEach((chargeableMeal: GetMealResponseDataDDChargeableMealListInner, index: number) => {
        for (let appliedMeal of parseEmptyArray<AppliedMealList>(this.appliedMeal?.appliedMealList)) {
          if (chargeableMeal.code === appliedMeal.code) {
            break;
          }
        }

        displayedPaidMealList.push({
          isActive: true,
          ...chargeableMeal,
          type: TravelerMealInfo.MealType.ANCILLARY,
          // 当該有料機内食情報.codeを引数として、「当画面共通処理No3.有料機内食名称取得処理」を行い、返却値の有料機内食名称を表示する。
          mealName: this._flightMealService.onHandleCommonNo3(chargeableMeal?.code ?? ''),
        });
      });
    }

    return displayedPaidMealList;
  }

  getDisplayedPreOrderMealList(mealData: GetMealResponseData) {
    let displayedPreOrderMealList: DisplayedPreOrderMealList[] = [];
    const isExistPreOrder = this.appliedMeal?.appliedMealList.some(
      (item: AppliedMealList) => item.type === TravelerMealInfo.MealType.PREORDER
    );
    const preorderMealList =
      (mealData?.[`${this.travelerId}`]?.[`${this.flightId}`]
        ?.preorderMealList as Array<GetMealResponseDataDDPreorderMealListInner>) ??
      ([] as Array<GetMealResponseDataDDPreorderMealListInner>);

    if (isExistPreOrder) {
      preorderMealList.forEach((preOrderMeal: GetMealResponseDataDDPreorderMealListInner, index: number) => {
        displayedPreOrderMealList.push({
          isActive: true,
          ...preOrderMeal,
          type: TravelerMealInfo.MealType.PREORDER,
        });
      });
    } else {
      // 上記以外の場合、全ての事前オーダーミール情報.codeを活性で表示する。
      displayedPreOrderMealList = preorderMealList?.map((preOrderMeal: GetMealResponseDataDDPreorderMealListInner) => {
        for (let appliedMeal of parseEmptyArray<AppliedMealList>(this.appliedMeal?.appliedMealList)) {
          if (preOrderMeal.code === appliedMeal.code) {
            break;
          }
        }
        return {
          isActive: true,
          ...preOrderMeal,
          type: TravelerMealInfo.MealType.PREORDER,
        };
      });
    }
    return displayedPreOrderMealList;
  }

  getDisplayedSpecialMealList(mealData: GetMealResponseData) {
    let displayedSpecialMealList: DisplayedSpecialMealList[] = [];
    const isExistSpecial = this.appliedMeal?.appliedMealList?.some(
      (item: AppliedMealList) => item.type === TravelerMealInfo.MealType.SPECIAL
    );
    const specialMealListKeys =
      Object.keys(mealData?.[String(this.travelerId)]?.[String(this.flightId)]?.specialMealList) ?? [];

    if (isExistSpecial) {
      specialMealListKeys.forEach((keys: string) => {
        const title = this._flightMealService.onHandleCommonNo7(keys);
        const listSpecialMeal: Array<ListSpecialMeal> = [];

        // 申込済機内食情報が存在するかつ、申込済機内食情報.type=""special"" (事前機内食)の場合、申込済機内食情報.code=カテゴリ別特別機内食情報.codeとなる機内食メニューのみ活性とし、他のカテゴリ別特別機内食情報.codeを非活性で表示する。
        mealData?.[`${this.travelerId}`]?.[`${this.flightId}`]?.specialMealList[keys]?.forEach(
          (specialMeals: InnerSpecialMealList) => {
            specialMeals?.code?.forEach((code: string) => {
              listSpecialMeal.push({
                type: TravelerMealInfo.MealType.SPECIAL,
                code: code,
                mealName: this._flightMealService.onHandleCommonNo4(code),
                isActive: true,
              });
            });
          }
        );
        displayedSpecialMealList.push({
          title,
          listSpecialMeal,
        });
      });
    } else {
      // 上記以外の場合、カテゴリ別特別機内食情報.codeを活性で表示する。
      specialMealListKeys.forEach((keys: string) => {
        const title = this._flightMealService.onHandleCommonNo7(keys);
        const listSpecialMeal: Array<ListSpecialMeal> = [];
        mealData?.[`${this.travelerId}`]?.[`${this.flightId}`]?.specialMealList[keys].forEach(
          (specialMeals: InnerSpecialMealList) => {
            specialMeals?.code?.forEach((code: string) => {
              listSpecialMeal.push({
                type: GetMealResponseDataDDAppliedMealListInner.TypeEnum.Special,
                code: code,
                mealName: this._flightMealService.onHandleCommonNo4(code),
                isActive: true,
              });
            });
          }
        );

        displayedSpecialMealList.push({
          title,
          listSpecialMeal,
        });
      });
    }
    return displayedSpecialMealList;
  }

  /**(8) 機内食メニュー(有料機内食)※機内食メニュー選択モーダル */
  /**
   * @param displayedMeal
   */
  handleMealMenu(displayedMeal: DisplayedPaidMealList) {
    // (8.1) 機内食メニュー選択モーダルにて選択した機内食に紐づく機内食種別、SSRコード、有料サービス金額を取得する。
    // (8.2) 画面.機内食選択モーダル画面に紐づくセグメントID、搭乗者IDを取得する。
    // (8.3) 搭乗者ID、セグメントIDを基に、セグメントマップと搭乗者別申込状況マップをチェックして、存在しない場合：追加する、存在した場合：更新する。
    const changedData = {
      updateCategory: TravelerMealInfo.UpdateCategory.REQUEST,
      // 機内食種別
      mealType: displayedMeal?.type,
      // SSRコード
      ssrCode: displayedMeal?.code,
      // 有料サービス金額
      paidServiceAmount: displayedMeal?.prices?.total ?? 0,
    } as PassengerApplicationStatusMap;

    // 有料機内食カタログ情報
    // セグメントマップ
    this._flightMealService.mealInformation = this.updateOrInsertMealInformation(
      this.flightId,
      this.travelerId,
      changedData
    );

    // 搭乗者別申込状況マップ
    if (this._flightMealService.mealInformation.segmentMap?.get(this.flightId)?.mealCatalog?.length === 0) {
      const chargeableMealList =
        (this.meal?.[`${this.travelerId}`]?.[`${this.flightId}`]
          ?.chargeableMealList as Array<GetMealResponseDataDDChargeableMealListInner>) ??
        ([] as Array<GetMealResponseDataDDChargeableMealListInner>);

      chargeableMealList?.forEach((item: GetMealResponseDataDDChargeableMealListInner) => {
        this._flightMealService.mealInformation.segmentMap?.get(this.flightId)?.mealCatalog?.push({
          // SSRコード
          ssrCode: item.code,
          // 在庫数
          quota: item.quota,
        });
      });
    }
    // (8.4) 選択した機内食にひもづく「機内食選択」（表示内容シートNo.9）を選択済みにする。
    this._flightMealService._reRender.next(true);
    // (8.5) 機内食メニュー選択モーダルを閉じ、機内食選択モーダルを表示する。
    this.clickCloseSub();
    // (8.6) 「当画面共通処理No5.申込ボタン状態処理」を行う。
    this._flightMealService.onHandleCommonNo5();
  }

  /**(9) 機内食メニュー(事前ミール)※機内食メニュー選択モーダル*/
  /**
   * @param displayedPreOrderMeal
   */
  handlePreMealMenu(displayedPreOrderMeal: DisplayedPreOrderMealList) {
    // (9.1) 機内食メニュー選択モーダルにて選択した機内食に紐づく機内食種別、SSRコード、事前ミールメッセージIDを取得する。
    // (9.2) 画面.機内食選択モーダル画面に紐づくセグメントID、搭乗者IDを取得する。
    // (9.3) 搭乗者ID、セグメントIDを基に、セグメントマップと搭乗者別申込状況マップをチェックして、存在しない場合：追加する、存在した場合：更新する。
    const changedData = {
      updateCategory: TravelerMealInfo.UpdateCategory.REQUEST,
      // 機内食種別
      mealType: displayedPreOrderMeal?.type,
      // SSRコード
      ssrCode: displayedPreOrderMeal?.code,
      // 事前ミールメッセージID
      mealMessageId: displayedPreOrderMeal?.key,
    };

    this._flightMealService.mealInformation = this.updateOrInsertMealInformation(
      this.flightId,
      this.travelerId,
      changedData
    );
    // (9.4) 選択した機内食にひもづく「機内食選択」（表示内容シートNo.9）を選択済みにする。
    this._flightMealService._reRender.next(true);
    // (9.5) 機内食メニュー選択モーダルを閉じ、機内食選択モーダルを表示する。
    this.clickCloseSub();
    // (9.6) 「当画面共通処理No5.申込ボタン状態処理」を行う。
    this._flightMealService.onHandleCommonNo5();
  }

  /** (10) 機内食メニュー(特別機内食)※機内食メニュー選択モーダル */
  async onChangeSpecialMeal(mealType: string, ssrCode: string, flightId: string, travelerId: string) {
    // (10.1)	機内食メニュー選択モーダルにて選択した機内食に紐づく機内食種別、SSRコードを取得する。-> it is mealType & ssrCode on parameter
    // (10.2)	画面.機内食選択モーダル画面に紐づくセグメントID、搭乗者IDを取得する。-> it is flightId & travelerId on parameter
    // (10.3) 搭乗者ID、セグメントIDを基に、セグメントマップと搭乗者別申込状況マップをチェックして、存在しない場合：追加する、存在した場合：更新する
    const passengerData: PassengerApplicationStatusMap = {
      mealType: mealType,
      ssrCode: ssrCode,
      updateCategory: TravelerMealInfo.UpdateCategory.REQUEST,
    };

    this._flightMealService.mealInformation = this.updateOrInsertMealInformation(flightId, travelerId, passengerData);

    // (10.4)	選択した機内食にひもづく「機内食選択」（表示内容シートNo.9）を選択済みにする。
    // Handle at flight-meal-detail.component.ts [Func: haveMealSelected]

    // (10.5)	以下の条件をすべて満たす場合、2便目以降も同じお食事を選択する旨(メッセージID:"MSG1012")を確認ダイアログ(G02-005)に渡し、機内食一括申込ダイアログを表示する。
    if (this.selectSameMealForAllFlight(flightId, travelerId)) {
      // (10.6)	機内食一括申込ダイアログからの返却値を確認する
      const result = await this._myBookingServiceService.openDialogPromise('MSG1012');
      // (10.6.1)	機内食一括申込ダイアログより閉じるボタン押下が返却された場合、処理を終了する。
      // (10.6.2)	機内食一括申込ダイアログより確定ボタン押下が返却された場合、以下の通り処理を行う。
      if (result === DialogClickType.CONFIRM) {
        // (10.6.2.1)	更新サービス情報に以下の内容を設定する。
        const flightsList = this._flightMealService.flightsList;
        // ＜画面.セグメント別機内食選択の要素(以降、搭乗者対象セグメント)数分繰り返し＞
        flightsList.forEach((flight: Type1) => {
          // (10.6.2.2)	更新サービス情報に以下の内容を設定する。
          const passengerData: PassengerApplicationStatusMap = {
            mealType: mealType,
            ssrCode: ssrCode,
            updateCategory: TravelerMealInfo.UpdateCategory.REQUEST,
          };
          this._flightMealService.mealInformation = this.updateOrInsertMealInformation(
            flight?.id!,
            travelerId,
            passengerData
          );
        });
      }
    }

    // (10.7) 機内食メニュー選択モーダルを閉じ、機内食選択モーダルを表示する
    this.clickCloseSub();
    this._flightMealService._reRender.next(true);
    // (10.8)「当画面共通処理No5.申込ボタン状態処理」を行う。
    this._flightMealService.onHandleCommonNo5();
  }

  selectSameMealForAllFlight(flightId: string, travelerId: string): boolean {
    const flightsList = this._flightMealService.flightsList;
    // (10.5.1)	画面.セグメント別機内食選択の要素数>1 かつ 選択した機内食が画面.セグメント別機内食選択[0]であること
    if (flightsList.length <= 1 || flightsList?.[0]?.id !== flightId) {
      return false;
    }

    // (10.5.2)	機内食情報取得API応答.data.<搭乗者ID>となるappliedMealListにSSRコードが存在しない
    const getMealFlight = this.meal?.[travelerId] as GetMealResponseDataD;
    let isExistSSRCode = false;
    for (let flightId of Object.keys(getMealFlight)) {
      if (getMealFlight?.[flightId]?.appliedMealList && getMealFlight?.[flightId]?.appliedMealList?.length > 0) {
        isExistSSRCode = getMealFlight?.[flightId]?.appliedMealList?.some(
          (appliedMeal: GetMealResponseDataDDAppliedMealListInner) => Boolean(appliedMeal?.code)
        );
      }
      if (isExistSSRCode) {
        break;
      }
    }

    if (isExistSSRCode) {
      return false;
    }

    // (10.5.3)	画面.セグメント別機内食選択の要素内に、取得した搭乗者IDに紐づく画面.セグメント別機内食選択.機内食選択が押下不可となるセグメントが存在しない
    if (this.isAllSelectable(travelerId) !== true) {
      return false;
    }

    return true;
  }

  isAllSelectable(travelerId: string): boolean {
    let enableSelectAll = true;

    const flightsList = this._flightMealService.flightsList;
    flightsList.forEach((flight: Type1) => {
      if (this._flightMealService.canSelectAllMealByTravelers[travelerId][flight?.id!] === false) {
        enableSelectAll = false;
      }
    });

    return enableSelectAll;
  }

  /** (11) 機内食辞退ボタン※機内食メニュー選択モーダル */
  onRefuseInFlightMeal(flightId: string, travelerId: string) {
    // (11.1)	画面.機内食選択モーダル画面に紐づくセグメントID、搭乗者IDを取得する。-> it is flightId & travelerId on parameter
    // (11.2) 搭乗者ID、セグメントIDを基に、セグメントマップと搭乗者別申込状況マップをチェックして、存在しない場合：追加する、存在した場合：更新する。)
    const passengerData: PassengerApplicationStatusMap = {
      mealType: TravelerMealInfo.MealType.NOTMEAL,
      ssrCode: TravelerMealInfo.MealCode.NTML,
      updateCategory: TravelerMealInfo.UpdateCategory.REQUEST,
    };

    this._flightMealService.mealInformation = this.updateOrInsertMealInformation(flightId, travelerId, passengerData);

    // (11.3) 選択した機内食を選択済みにする。
    this._flightMealService._reRender.next(true);

    // (11.4)	機内食メニュー選択モーダルを閉じ、機内食選択モーダルを表示する。
    this.clickCloseSub();

    // (11.5)「当画面共通処理No5.申込ボタン状態処理」を行う
    this._flightMealService.onHandleCommonNo5();
  }

  /** (3) 閉じるボタン※機内食メニュー選択モーダル */
  clickClose() {
    this.clickCloseSub();
  }

  updateOrInsertMealInformation(flightId: string, travelerId: string, passengerData: PassengerApplicationStatusMap) {
    let mealInformation = { ...this._flightMealService.mealInformation };
    if (!mealInformation?.segmentMap) {
      mealInformation.segmentMap = new Map<string, SegmentMap>();
    }

    if (!mealInformation?.segmentMap?.has(flightId)) {
      mealInformation?.segmentMap?.set(flightId, {
        passengerApplicationStatusMap: new Map<string, PassengerApplicationStatusMap>(),
        mealCatalog: [] as MealCatalogItem[],
      });
    }

    mealInformation?.segmentMap?.get(flightId)?.passengerApplicationStatusMap?.set(travelerId, passengerData);
    return mealInformation;
  }

  isDisplaySoldOut(quota: number): boolean {
    return !quota || quota === 0;
  }

  /** 閉じる処理 */
  clickCloseSub() {
    this.close();
  }

  override reload(): void {}

  override destroy(): void {
    this._subscriptions?.unsubscribe();
  }
}
